import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, Copy, Search, Lock, Shield, Settings, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  { name: 'Bancos', icon: '🏦' }, { name: 'Email', icon: '📧' }, { name: 'Redes Sociais', icon: '📱' },
  { name: 'Trabalho', icon: '💼' }, { name: 'Streaming', icon: '🎬' }, { name: 'Compras', icon: '🛒' },
  { name: 'Cloud', icon: '☁️' }, { name: 'Governo', icon: '🏛️' }, { name: 'Seguros', icon: '🛡️' }, { name: 'Outros', icon: '🔑' }
];

const PasswordVaultTab = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('vault_sort') || 'custom');

  const updateSort = (val) => {
    setSortBy(val);
    localStorage.setItem('vault_sort', val);
  };
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [newCatName, setNewCatName] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('🔑');
  const [form, setForm] = useState({ type: 'empresa', name: '', category: 'Outros', username: '', password: '', url: '', notes: '' });
  const [viewItem, setViewItem] = useState(null);

  const catIcons = {};
  categories.forEach(c => { catIcons[c.name] = c.icon; });

  const tryBiometric = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        const result = await NativeBiometric.isAvailable();
        if (result.isAvailable) {
          await NativeBiometric.verifyIdentity({ reason: 'Acessar Cofre de Senhas', title: 'Cofre de Senhas', subtitle: 'Use biometria para desbloquear' });
          setUnlocked(true);
          loadItems();
          loadCategories();
          return true;
        }
      } catch (e) {
        console.log('Biometria falhou ou cancelada:', e);
      }
    }
    return false;
  };

  useEffect(() => { tryBiometric(); }, []);

  const verify = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/vault/verify`, { password: verifyPassword });
      setUnlocked(true);
      loadItems();
      loadCategories();
    } catch (e) {
      toast.error('Senha incorreta');
    } finally { setVerifying(false); }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/vault/passwords`);
      // Atribuir sort_order sequencial se estiverem duplicados
      const sorted = (data || []).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      sorted.forEach((item, i) => { item.sort_order = i; });
      setItems(sorted);
    } catch (e) {} finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/vault/categories`);
      if (data && data.length > 0) {
        setCategories([...DEFAULT_CATEGORIES, ...data.filter(d => !DEFAULT_CATEGORIES.some(dc => dc.name === d.name))]);
      }
    } catch (e) {}
  };

  const addCategory = async () => {
    if (!newCatName) return;
    try {
      await axios.post(`${API}/admin/vault/categories`, { name: newCatName, icon: newCatIcon });
      toast.success('Categoria criada');
      setNewCatName(''); setNewCatIcon('🔑');
      loadCategories();
    } catch (e) { toast.error('Erro'); }
  };

  const removeCategory = async (id) => {
    try { await axios.delete(`${API}/admin/vault/categories/${id}`); toast.success('Removida'); loadCategories(); } catch (e) {}
  };

  const openNew = () => { setEditing(null); setForm({ type: 'empresa', name: '', category: 'Outros', username: '', password: '', url: '', notes: '' }); setShowModal(true); };
  const openEdit = (item) => { setEditing(item); setForm({ type: item.type, name: item.name, category: item.category, username: item.username, password: item.password, url: item.url || '', notes: item.notes || '' }); setShowModal(true); };

  const save = async () => {
    if (!form.name || !form.username) { toast.error('Preencha nome e usuário'); return; }
    try {
      if (editing) { await axios.put(`${API}/admin/vault/passwords/${editing.id}`, form); toast.success('Atualizado'); }
      else { await axios.post(`${API}/admin/vault/passwords`, form); toast.success('Salvo'); }
      setShowModal(false); loadItems();
    } catch (e) { toast.error('Erro ao salvar'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta senha?')) return;
    try { await axios.delete(`${API}/admin/vault/passwords/${id}`); toast.success('Removido'); loadItems(); } catch (e) { toast.error('Erro'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const toggleVisible = (id) => setVisiblePasswords(prev => ({ ...prev, [id]: !prev[id] }));

  const stripSpecial = (str) => (str || '').replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase();

  const filtered = items.filter(i => {
    if (filterType !== 'all' && i.type !== filterType) return false;
    if (filterCategory !== 'all' && i.category !== filterCategory) return false;
    if (search) {
      const s = stripSpecial(search);
      return stripSpecial(i.name).includes(s);
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'category') return (a.category || '').localeCompare(b.category || '');
    if (sortBy === 'type') return (a.type || '').localeCompare(b.type || '');
    if (sortBy === 'recent') return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
    // custom: usar sort_order
    return (a.sort_order || 999) - (b.sort_order || 999);
  });

  const moveItem = async (id, direction) => {
    // Trabalhar com a lista filtrada/ordenada atual
    const list = [...filtered];
    const idx = list.findIndex(i => i.id === id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    
    // Trocar na lista
    [list[idx], list[targetIdx]] = [list[targetIdx], list[idx]];
    
    // Atribuir sort_order baseado na nova posição
    const updates = {};
    list.forEach((item, i) => { updates[item.id] = i; });
    
    // Atualizar state imediatamente
    setItems(prev => prev.map(i => updates[i.id] !== undefined ? { ...i, sort_order: updates[i.id] } : i));
    
    // Salvar no backend
    try {
      await Promise.all([
        axios.put(`${API}/admin/vault/passwords/${list[idx].id}/sort`, { sort_order: updates[list[idx].id] }),
        axios.put(`${API}/admin/vault/passwords/${list[targetIdx].id}/sort`, { sort_order: updates[list[targetIdx].id] })
      ]);
    } catch (e) { toast.error('Erro ao mover'); }
  };

  // Tela de desbloqueio
  if (!unlocked) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="bg-white border rounded-2xl p-8 shadow-lg max-w-sm w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-xl font-bold mb-1">Cofre de Senhas</h2>
          <p className="text-sm text-gray-500 mb-6">Digite sua senha para acessar</p>
          <input
            type="password"
            className="w-full border rounded-lg px-4 py-3 text-center text-lg mb-4"
            placeholder="Senha"
            value={verifyPassword}
            onChange={e => setVerifyPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && verify()}
            autoFocus
          />
          <Button onClick={verify} disabled={verifying || !verifyPassword} className="w-full bg-indigo-600 hover:bg-indigo-700 py-3">
            {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
            {verifying ? 'Verificando...' : 'Desbloquear'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold">Cofre de Senhas</h2>
            <Badge className="bg-green-100 text-green-700 text-xs">{items.length}</Badge>
          </div>
          <Button onClick={openNew} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Nova Senha</span><span className="sm:hidden">Nova</span>
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => {
            const allVisible = items.every(i => visiblePasswords[i.id]);
            const newState = {};
            items.forEach(i => { newState[i.id] = !allVisible; });
            setVisiblePasswords(newState);
          }}>
            {items.every(i => visiblePasswords[i.id]) ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
            {items.every(i => visiblePasswords[i.id]) ? 'Ocultar' : 'Mostrar'}
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowCatModal(true)}>
            <Settings className="h-3 w-3 mr-1" /> Categorias
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { setUnlocked(false); setVerifyPassword(''); setItems([]); }}>
            <Lock className="h-3 w-3 mr-1" /> Bloquear
          </Button>
          {/* Botão temporário para re-salvar senhas antigas */}
          {items.some(i => i.password && i.password.startsWith('gAAAA')) && (
            <Button 
              variant="outline" 
              size="sm" 
              className="text-xs h-7 border-red-300 text-red-600 hover:bg-red-50"
              onClick={async () => {
                const broken = items.filter(i => i.password && i.password.startsWith('gAAAA'));
                if (!window.confirm(`${broken.length} senha(s) encriptada(s) serão APAGADAS. Você precisará re-digitar. Continuar?`)) return;
                try {
                  for (const item of broken) {
                    await axios.put(`${API}/admin/vault/passwords/${item.id}`, { ...item, password: '' });
                  }
                  toast.success(`${broken.length} senha(s) limpas. Edite cada uma para salvar a senha nova.`);
                  loadItems();
                } catch (e) {
                  toast.error('Erro: ' + (e.response?.data?.detail || 'Falha'));
                }
              }}
            >
              🗑️ Limpar senhas quebradas
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-1.5">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
          <input className="w-full border rounded-lg pl-8 pr-3 py-1.5 text-xs sm:text-sm" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="border rounded-lg px-2 py-1.5 text-xs sm:text-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">Todos</option>
          <option value="pessoal">Pessoal</option>
          <option value="empresa">Empresa</option>
        </select>
        <select className="border rounded-lg px-2 py-1.5 text-xs sm:text-sm" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="all">Categorias</option>
          {categories.map(c => <option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
        </select>
        <select className="border rounded-lg px-3 py-2 text-sm" value={sortBy} onChange={e => updateSort(e.target.value)}>
          <option value="custom">Ordem Manual</option>
          <option value="name">Nome A-Z</option>
          <option value="category">Categoria</option>
          <option value="type">Tipo</option>
          <option value="recent">Mais Recente</option>
        </select>
      </div>

      {/* Lista */}
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div> : (
        <div className="space-y-2">
          {filtered.length === 0 ? <p className="text-center text-gray-400 py-12">Nenhuma senha encontrada</p> : filtered.map(item => (
            <div key={item.id} className="bg-white border rounded-lg p-2.5 sm:p-4 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setViewItem(item)}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg">{catIcons[item.category] || '🔑'}</span>
                    <span className="font-semibold">{item.name}</span>
                    <Badge variant="outline" className="text-xs">{item.category}</Badge>
                    <Badge className={`text-xs ${item.type === 'pessoal' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{item.type === 'pessoal' ? 'Pessoal' : 'Empresa'}</Badge>
                  </div>
                  <div className="mt-1.5 space-y-0.5">
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                      <span className="text-gray-500 w-14 sm:w-16 flex-shrink-0">Usuário:</span>
                      <span className="font-mono text-gray-800 truncate">{item.username}</span>
                      <button onClick={(e) => { e.stopPropagation(); copyToClipboard(item.username); }} className="text-gray-400 hover:text-indigo-600 flex-shrink-0"><Copy className="h-3 w-3" /></button>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                      <span className="text-gray-500 w-14 sm:w-16 flex-shrink-0">Senha:</span>
                      <span className="font-mono text-gray-800 truncate">{visiblePasswords[item.id] ? item.password : '••••••••'}</span>
                      <button onClick={(e) => { e.stopPropagation(); toggleVisible(item.id); }} className="text-gray-400 hover:text-indigo-600 flex-shrink-0">
                        {visiblePasswords[item.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); copyToClipboard(item.password); }} className="text-gray-400 hover:text-indigo-600 flex-shrink-0"><Copy className="h-3 w-3" /></button>
                    </div>
                    {item.url && <div className="text-xs truncate"><span className="text-blue-600 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); window.open(item.url, '_blank'); }}>{item.url}</span></div>}
                    {item.notes && <div className="text-xs text-gray-500 truncate">{item.notes.split('\n')[0]}{item.notes.includes('\n') ? ' ...' : ''}</div>}
                  </div>
                </div>
                <div className="hidden sm:flex gap-1 ml-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {sortBy === 'custom' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => moveItem(item.id, -1)} className="h-8 w-8 p-0 text-gray-400" title="Mover para cima">↑</Button>
                      <Button size="sm" variant="outline" onClick={() => moveItem(item.id, 1)} className="h-8 w-8 p-0 text-gray-400" title="Mover para baixo">↓</Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(item)} className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(item.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Visualização */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto mx-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span>{catIcons[viewItem?.category] || '🔑'}</span>
              <span className="truncate">{viewItem?.name}</span>
              <Badge className={`text-xs ${viewItem?.type === 'pessoal' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{viewItem?.type === 'pessoal' ? 'Pessoal' : 'Empresa'}</Badge>
            </DialogTitle>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Categoria:</span><p className="font-semibold">{viewItem.category}</p></div>
                {viewItem.url && <div><span className="text-gray-500">URL:</span> <span className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => window.open(viewItem.url, '_blank')}>{viewItem.url}</span></div>}
              </div>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Usuário:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{viewItem.username}</span>
                    <button onClick={() => copyToClipboard(viewItem.username)} className="text-gray-400 hover:text-indigo-600"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Senha:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{visiblePasswords['view'] ? viewItem.password : '••••••••'}</span>
                    <button onClick={() => setVisiblePasswords(p => ({...p, view: !p.view}))} className="text-gray-400 hover:text-indigo-600">
                      {visiblePasswords['view'] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => copyToClipboard(viewItem.password)} className="text-gray-400 hover:text-indigo-600"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
              {viewItem.notes && (
                <div>
                  <span className="text-sm text-gray-500">Observações:</span>
                  <div className="text-sm whitespace-pre-line mt-1 max-h-[35vh] overflow-y-auto border rounded-lg p-2 bg-gray-50">{viewItem.notes}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={() => { const item = viewItem; setViewItem(null); remove(item.id); }} className="text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4 mr-1" /> Excluir
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setViewItem(null)}>Fechar</Button>
              <Button onClick={() => { const item = viewItem; setViewItem(null); openEdit(item); }} className="bg-indigo-600 hover:bg-indigo-700">
                <Pencil className="h-4 w-4 mr-1" /> Editar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Editar Senha' : 'Nova Senha'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Tipo</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                  <option value="empresa">Empresa</option>
                  <option value="pessoal">Pessoal</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Categoria</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  {categories.map(c => <option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Nome do Serviço</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: Gmail, Banco do Brasil" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Usuário / Email</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="Ex: email@gmail.com" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Senha</label>
              <input type="password" className="w-full border rounded px-3 py-2 text-sm" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editing ? '(manter atual)' : 'Senha'} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">URL (opcional)</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Observações (opcional)</label>
              <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[80px]" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notas adicionais" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Categorias */}
      <Dialog open={showCatModal} onOpenChange={setShowCatModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Gerenciar Categorias</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {categories.map((c, i) => (
              <div key={c.id || c.name} className="flex items-center justify-between p-2 border rounded">
                <span className="text-sm">{c.icon} {c.name}</span>
                {c.id && <button onClick={() => removeCategory(c.id)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <div className="flex gap-2">
              <input className="w-12 border rounded px-2 py-1.5 text-center text-lg" value={newCatIcon} onChange={e => setNewCatIcon(e.target.value)} />
              <input className="flex-1 border rounded px-3 py-1.5 text-sm" placeholder="Nome da categoria" value={newCatName} onChange={e => setNewCatName(e.target.value)} />
              <Button size="sm" onClick={addCategory} disabled={!newCatName} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PasswordVaultTab;
